import { config } from '../config';
import type { LogEntryRow, LogLevel } from '../types';
import { db } from '../data/harnessDb';
import { describeUnknownError, StorageError } from './errors';

/**
 * OBS-1 — structured logger with module category, level and matchId correlation. Console in dev,
 * a capped ring buffer of `LOG_RING_CAP` entries in IndexedDB in production. Global `error` and
 * `unhandledrejection` handlers write to it (see `./globalHandlers.ts`).
 *
 * ── Why this file is written the way it is ───────────────────────────────────
 * Pattern: `fire-and-forget-observability`. The logger must never block the critical path. In this
 * build unit that is not a general principle but a correctness requirement: **an IndexedDB write
 * from the logger inside the measured window would corrupt the very measurement U-0 exists to
 * take.** Design §12.1 says it plainly — "a per-increment IDB write would double the write rate
 * inside NFR-1's budget and the p95 the debug screen reports would be measuring the logger."
 *
 * So: entries buffer in memory and flush on §12.1a's four triggers, each covering a failure the
 * others cannot:
 *
 * ```
 * buffer.length ≥ LOG_FLUSH_THRESHOLD  → flush now   bounds memory and burst loss        (U-0)
 * buffer non-empty, quiet for          → flush once  bounds FOREGROUND loss in TIME      (U-20)
 *   LOG_FLUSH_IDLE_MS, at most one
 *   outstanding deferral at a time
 * visibilitychange → hidden · pagehide → flush now   the BACKGROUND kill boundary        (R-5)
 * every Match.state transition         → flush now   correlates the log with the match   (U-20)
 * ```
 *
 * The third is wired in `bootstrap.ts` and the fourth in `bus.ts`, at the one place a `Match.state`
 * write can be observed. The runner calls `pauseFlush()` for the duration of a measured run and
 * `resumeFlush()` after, and the run record carries `logFlushesDuringRun` — a count that must be
 * zero — so the claim is checkable in the exported data rather than merely asserted.
 *
 * **`requestIdleCallback` is not one of the live triggers on the target platform** — see the note on
 * `scheduleIdleFlush` below, and §0.3.
 *
 * ── The one sanctioned exception to NFR-8 ────────────────────────────────────
 * A failure to *persist* a log entry is reported to the console and swallowed at that single
 * boundary. NFR-8 forbids silent catches; this catch is not silent (it reports) and it is the
 * documented exception design §12.1 names: a lost log entry is strictly preferable to a failed
 * flush taking down a capture path. It is the only place in the codebase that swallows.
 */

export interface LoggerOptions {
  /** Console output. Vite sets `import.meta.env.DEV`; tests pass it explicitly. */
  console: boolean;
  /** Persist the ring buffer. Disabled in unit tests that are not exercising persistence. */
  persist: boolean;
  now: () => number;
}

const buffer: LogEntryRow[] = [];
let flushPaused = false;
let flushesSinceTrim = 0;
let totalFlushes = 0;
let idleHandle: number | null = null;
/** §12.1a's deferred flush. At most one outstanding, and never one while the buffer is empty. */
let deferHandle: ReturnType<typeof setTimeout> | null = null;
let inFlight: Promise<void> | null = null;
/**
 * Whether this page load has already re-anchored the `seq` generator — see {@link anchorBatch}.
 * Per **load**, not per flush: the platform behaviour it repairs can only be introduced by a page
 * teardown, so one repair per load is exactly enough and one indexed read is its whole cost.
 */
let generatorAnchored = false;

let options: LoggerOptions = {
  console: true,
  persist: false,
  now: () => Date.now(),
};

/** Correlation id threaded onto every entry — OBS-1's `matchId`. U-0 threads the harness run id. */
let correlationId: string | null = null;

export function configureLogger(next: Partial<LoggerOptions>): void {
  options = { ...options, ...next };
}

export function setCorrelationId(id: string | null): void {
  correlationId = id;
}

/** Number of flushes performed since the counter was last reset. Proof for `logFlushesDuringRun`. */
export function flushCount(): number {
  return totalFlushes;
}

export function resetFlushCount(): void {
  totalFlushes = 0;
}

/**
 * Suspends persistence. Buffering continues, so nothing is lost — the entries land on resume.
 * Called around every measured window.
 */
export function pauseFlush(): void {
  flushPaused = true;
  cancelIdle();
  cancelDeferred();
}

export function resumeFlush(): void {
  flushPaused = false;
}

export function isFlushPaused(): boolean {
  return flushPaused;
}

/** Entries not yet persisted. Exposed for the debug view and for tests. */
export function pending(): readonly LogEntryRow[] {
  return buffer;
}

function cancelIdle(): void {
  if (idleHandle === null) return;
  const w = globalThis as unknown as { cancelIdleCallback?: (h: number) => void };
  if (typeof w.cancelIdleCallback === 'function') w.cancelIdleCallback(idleHandle);
  idleHandle = null;
}

function cancelDeferred(): void {
  if (deferHandle === null) return;
  clearTimeout(deferHandle);
  deferHandle = null;
}

/**
 * §12.1a's deferred flush — *"buffer non-empty, no flush for `LOG_FLUSH_IDLE_MS` → flush once, at
 * most one outstanding deferral at a time"*.
 *
 * ── Why a timer is legitimate here and forbidden for nudges (§2.4) ───────────
 *
 * §2.4 bans `setTimeout` for **deadlines**, because a timer armed before the phone goes into a
 * pocket may never fire and a nudge that never appears is a missed requirement. Here the failure
 * mode is the opposite: if this is throttled away while backgrounded, the `visibilitychange →
 * hidden` / `pagehide` trigger has **already flushed** at the moment of backgrounding. The two
 * triggers partition the problem — this one owns the foreground, the lifecycle events own the
 * transition out of it — and neither has to cover the other's case.
 *
 * ── Why it is not the polling NFR-5 forbids ─────────────────────────────────
 *
 * NFR-5 bans work that recurs on a cadence *whether or not there is anything to do*. This timer
 * exists only while unflushed entries exist, is limited to one outstanding deferral, and is
 * cancelled by any other trigger. §12.1a names the assertable form of that — **"no timer is
 * outstanding when the buffer is empty"** — because a code-absence check cannot tell a one-shot
 * continuation from an interval. {@link hasDeferredFlush} is that state, exposed to be read.
 */
function scheduleDeferredFlush(): void {
  if (flushPaused || !options.persist || deferHandle !== null || buffer.length === 0) return;
  deferHandle = setTimeout(() => {
    deferHandle = null;
    void flush();
  }, config.LOG_FLUSH_IDLE_MS);
}

/**
 * Whether a deferred flush is outstanding. §12.1a's negative is asserted against this, not against
 * the absence of a `setTimeout` call — see {@link scheduleDeferredFlush}.
 */
export function hasDeferredFlush(): boolean {
  return deferHandle !== null;
}

/**
 * A **progressive enhancement that is absent on the target runtime**, not a fallback path.
 *
 * This guard used to carry the comment *"Safari <17 lacks it; the size threshold covers us"*. Both
 * halves were wrong. `requestIdleCallback` has **never shipped enabled-by-default in any Safari**,
 * desktop or iOS (§0.3, verified 2026-08-02) — so this is not a floor the runtime rises above at
 * some version, it is a capability iOS Safari does not have at all. And what the size threshold
 * "covers" is a burst; the loss this trigger was supposed to bound is a *quiet* period ending in a
 * jetsam kill, which no count threshold reaches.
 *
 * The guard stays, because a capability probe is the only shape §0.3 permits at a call site and
 * because the API is genuinely useful where it exists. What changes is its standing: §12.1a requires
 * that **disabling `requestIdleCallback` entirely changes no acceptance** — it is never the only
 * path to a flush. The live triggers on the target are the size threshold and the
 * `visibilitychange → hidden` / `pagehide` lifecycle flush wired in `bootstrap.ts`; the deferred
 * `LOG_FLUSH_IDLE_MS` flush and the per-transition flush are U-20's.
 *
 * The generalisable half is §0.3's rule: a platform-version claim in a comment is an assertion no
 * test evaluates, so a reader checks the guard, reads the reassurance, and stops looking.
 */
function scheduleIdleFlush(): void {
  if (flushPaused || !options.persist || idleHandle !== null) return;
  const w = globalThis as unknown as {
    requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
  };
  if (typeof w.requestIdleCallback !== 'function') return;
  idleHandle = w.requestIdleCallback(
    () => {
      idleHandle = null;
      void flush();
    },
    { timeout: 2000 },
  );
}

function emit(level: LogLevel, module: string, msg: string, data?: Record<string, unknown>): void {
  const entry: LogEntryRow = {
    ts: options.now(),
    level,
    module,
    matchId: correlationId,
    msg,
    data: data ?? null,
  };

  buffer.push(entry);

  if (options.console) {
    const line = `[${module}]${correlationId ? ` (${correlationId})` : ''} ${msg}`;
    // eslint-disable-next-line no-console -- OBS-1 requires console output in dev.
    (level === 'error' ? console.error : level === 'warn' ? console.warn : console.log)(line, data ?? '');
  }

  if (buffer.length >= config.LOG_FLUSH_THRESHOLD) {
    void flush();
  } else {
    // Both are "not yet a burst" paths. The idle callback is the enhancement that is absent on the
    // target (see above); the deferral is §12.1a's live foreground bound. Arming both is not two
    // mechanisms — whichever fires first calls `flush()`, which cancels the other.
    scheduleIdleFlush();
    scheduleDeferredFlush();
  }
}

export const log = {
  debug: (module: string, msg: string, data?: Record<string, unknown>) =>
    emit('debug', module, msg, data),
  info: (module: string, msg: string, data?: Record<string, unknown>) =>
    emit('info', module, msg, data),
  warn: (module: string, msg: string, data?: Record<string, unknown>) =>
    emit('warn', module, msg, data),
  error: (module: string, msg: string, data?: Record<string, unknown>) =>
    emit('error', module, msg, data),
  /** Convenience for `catch` blocks: never loses the original, never swallows. */
  exception: (module: string, msg: string, err: unknown, data?: Record<string, unknown>) =>
    emit('error', module, msg, { ...(data ?? {}), error: describeUnknownError(err) }),
};

/**
 * Drains the in-memory buffer into IndexedDB and amortises ring trimming.
 *
 * Never rejects. That is deliberate and is the sanctioned NFR-8 exception documented at the top of
 * this file: the failure is reported to the console, the entries are dropped rather than retried
 * forever, and the caller — which is always a fire-and-forget `void flush()` — continues.
 */
export async function flush(): Promise<void> {
  // Any trigger cancels the deferral: §12.1a allows at most one outstanding, and leaving a stale one
  // armed after a size-threshold flush is how "no timer while the buffer is empty" stops holding.
  cancelDeferred();
  if (flushPaused || !options.persist || buffer.length === 0) return;
  if (inFlight) return inFlight;

  const batch = buffer.splice(0, buffer.length);
  totalFlushes++;
  flushesSinceTrim++;

  inFlight = (async () => {
    const handle = db();
    try {
      await handle.logEntries.bulkAdd(await anchorBatch(handle, batch));

      if (flushesSinceTrim >= config.LOG_TRIM_EVERY_N_FLUSHES) {
        flushesSinceTrim = 0;
        await trimRing(config.LOG_RING_CAP);
      }
    } catch (cause) {
      // ── The single sanctioned swallow (design §12.1). Reported, not silent. ──
      // eslint-disable-next-line no-console -- the logger cannot log its own persistence failure.
      console.error(
        '[logger] OBS-1 ring-buffer write failed; entries dropped. A lost log entry is preferable to a blocked capture path.',
        describeUnknownError(cause),
        await describeRing(handle, batch),
      );
    } finally {
      inFlight = null;
    }
  })();

  return inFlight;
}

/**
 * Repairs the `seq` key generator once per page load, by giving the batch's **first** row an
 * explicit key one above the highest stored one.
 *
 * ── The defect this closes, named and reproduced ────────────────────────────
 *
 * `logEntries.bulkAdd()` failed intermittently in the browser passes with
 * `BulkError: 32 of 32 operations failed · ConstraintError: Key already exists in the object store`,
 * roughly one loaded run in five. Three build cycles chased it and excluded ten mechanisms by
 * experiment. The mechanism is:
 *
 * > **WebKit commits the *records* of a write transaction that is still in flight when the page is
 * > torn down, but does not persist that transaction's auto-increment key-generator advance.**
 *
 * The next page load therefore opens a store holding rows `1..N` with the generator back at `1`,
 * and the next `add` with a generated key collides on every row — which is exactly the reading
 * {@link describeRing} captured: `{batchSize:32, batchWithSeq:0, storedCount:32, storedMin:1,
 * storedMax:32}`.
 *
 * Reproduced 20/20 in Playwright WebKit with raw IndexedDB and no Dexie, and isolated to the
 * teardown-inside-the-commit-window case by two green controls:
 *
 * | Arm | Sequence | Result |
 * |---|---|---|
 * | A | write 32 → **await `complete`** → reload → write 32 | 0/20 |
 * | B | write 32 → **do not await `complete`** → reload → write 32 | **20/20 `ConstraintError`** |
 * | C | write 32 → await `complete` → `close()` + reopen → write 32 | 0/20 |
 * | D | arm B, with this repair applied | 0/15 |
 *
 * **Why the product is in arm B and not arm A**, which is the part that took three cycles: `flush()`
 * awaits *Dexie's* promise, and this project has already established that Dexie's transaction
 * promise resolves **before** the native `IDBTransaction.complete` event in a real browser (see
 * `data/commit.ts`, which exists for that reason). So `flush()` returning is not the commit landing,
 * and a `page.reload()` — or a jetsam kill, or a home-screen swipe — inside that window is arm B.
 * Whether the teardown lands inside the window is the one-in-five.
 *
 * ── Why an explicit first key repairs it ────────────────────────────────────
 *
 * IndexedDB's *"generate a key"* rule: when a record is stored with an explicit numeric key greater
 * than or equal to the store's current number, the current number is set to that key **+ 1**. So one
 * explicit key at `max(storedKeys) + 1` moves the generator ahead of every stored row whether or not
 * it regressed, and the other 31 rows in the batch generate normally behind it.
 *
 * **This is a repair, not a retry.** It runs on the first flush of a page load, on the healthy path,
 * before any failure — and on a healthy run the explicit key is precisely the key the generator
 * would have produced anyway, so it changes nothing observable. It costs one indexed read per page
 * load, on a path that is never on the tap path (§12.1 — the logger never writes inside the tap
 * transaction).
 *
 * **Why not "await the `complete` event in `flush()`" instead** — the shape this project reaches for
 * elsewhere (`data/commit.ts`): it would not help. The teardown happens when it happens; awaiting
 * changes what the *logger* knows, not whether WebKit persisted the generator. Arm A is green
 * because its reload comes after the commit, not because it awaited.
 *
 * @returns the batch to write. Never throws: a failure to read the last key leaves the batch
 *   untouched and the write proceeds exactly as it did before — a repair that could take the flush
 *   down would be worse than the defect it repairs.
 */
async function anchorBatch(
  handle: ReturnType<typeof db>,
  batch: readonly LogEntryRow[],
): Promise<LogEntryRow[]> {
  const rows = [...batch];
  const first = rows[0];
  if (generatorAnchored || first === undefined) return rows;
  generatorAnchored = true;

  try {
    const lastKey = await handle.logEntries.orderBy('seq').lastKey();
    // An empty store needs no anchor: a fresh generator and a fresh store agree by construction.
    if (typeof lastKey === 'number') rows[0] = { ...first, seq: lastKey + 1 };
  } catch {
    // Deliberately not reported: this is a best-effort repair on a path whose *own* failure is
    // already reported by the swallow below, and a console line here would fire on every launch in
    // any runtime whose `logEntries` cannot be read at all.
  }
  return rows;
}

/**
 * What the ring actually held when a flush failed — the failure describing itself.
 *
 * Kept after {@link anchorBatch} named and closed the generator-regression mechanism, because it is
 * what turned that defect from a re-run into a reading, and because a *second* cause reaching this
 * store would present the same way. It costs one indexed read on a path that is already failing.
 *
 * ── It cannot make things worse ─────────────────────────────────────────────
 *
 * Reached only from the swallow, which is already off every measured path (`pauseFlush` brackets
 * measured windows, and NFR-1's budget is not open here). It never throws — a diagnostic that could
 * take down the capture path would invert the very rule §12.1 wrote the swallow for — and it never
 * calls {@link log}, so it cannot recurse into the failure it is describing.
 */
async function describeRing(
  handle: ReturnType<typeof db>,
  batch: readonly LogEntryRow[],
): Promise<Record<string, unknown>> {
  const batchWithSeq = batch.filter((entry) => 'seq' in entry).length;
  try {
    const keys = await handle.logEntries.orderBy('seq').primaryKeys();
    // A `put` of a sentinel would be a write on a failing store; reading the last key and adding one
    // is what the generator *should* hand out next, and comparing the two is the whole diagnosis.
    return {
      batchSize: batch.length,
      batchWithSeq,
      storedCount: keys.length,
      storedMin: keys[0] ?? null,
      storedMax: keys.at(-1) ?? null,
      dbName: handle.name,
    };
  } catch (cause) {
    return { batchSize: batch.length, batchWithSeq, ringUnreadable: describeUnknownError(cause) };
  }
}

/**
 * The object store the ring lives in, named **once** for readers outside this module.
 *
 * ── Why this constant exists at all (build unit B-22) ────────────────────────
 *
 * The milestone review found the OBS-1 log *written to one database and read from another*: this
 * module writes to the **harness** database (`config.HARNESS_DB_NAME` — see the note on the two
 * names in `config/constants.ts`), while both export paths read a store of the same name on the
 * **product** database, which nothing has ever written. So `debug/log.ndjson` was omitted from
 * every real archive (`serialize.ts` drops the entry entirely for an empty ring), and after a bad
 * match the log existed on the phone with **no export path carrying it** — retrievable only with a
 * Mac and Safari Web Inspector, which is not a pitch-side operation.
 *
 * The transferable half of that finding is that an observability signal has to be traced from its
 * producer to **every** consumer, and the consumers have to be checked against the producer's own
 * store rather than against a store with the same name. This module is the producer, so this is
 * where the pair (database, store) is declared; the two consumers reach the ring through
 * {@link readLogRing} (`data/repos/profile.ts` → BAK-1) and through this name against the
 * dynamic-schema connection (`app/recoveryExport.ts` → §13.5). Neither names a store of its own.
 */
export const LOG_RING_STORE = 'logEntries';

/**
 * The ring, oldest first — OBS-2's *"Debug data is included in the backup zip."*
 *
 * `orderBy('seq')` rather than `toArray()`: `seq` is the auto-incrementing insertion order, so this
 * is chronological, and an archive's bytes must not depend on the order a store happens to yield
 * (INV-12's byte-determinism is measured over exactly these bytes).
 *
 * ── Why this one reader may throw where the writer may not ───────────────────
 *
 * Everything above is fire-and-forget: a failure to *persist* an entry is reported and swallowed,
 * because a lost log entry is strictly preferable to a failed flush taking down a capture path
 * (§12.1, the module note). This is the opposite direction and carries the opposite obligation — a
 * BAK-1 export that silently produced an archive with no debug log would be the very defect B-22
 * exists to close, so a read failure is surfaced (NFR-8) and the export reports it. §13.5's rescue
 * path is the one caller that must **not** fail on it, and it degrades explicitly rather than by
 * catching this one (see `app/recoveryExport.ts`).
 *
 * @throws {StorageError} when the ring cannot be read.
 */
export async function readLogRing(): Promise<LogEntryRow[]> {
  try {
    return await db().logEntries.orderBy('seq').toArray();
  } catch (cause) {
    throw new StorageError('Could not read the OBS-1 diagnostic log for an export.', {
      cause,
      context: { db: config.HARNESS_DB_NAME, store: LOG_RING_STORE },
    });
  }
}

/**
 * OBS-1's cap. Trims from the low end of the auto-incrementing `seq`, which is insertion order.
 * Amortised: called on every `LOG_TRIM_EVERY_N_FLUSHES`th flush, not every flush.
 */
export async function trimRing(cap: number): Promise<number> {
  const handle = db();
  const total = await handle.logEntries.count();
  const excess = total - cap;
  if (excess <= 0) return 0;

  const oldest = await handle.logEntries.orderBy('seq').limit(excess).primaryKeys();
  await handle.logEntries.bulkDelete(oldest);
  return oldest.length;
}

/** Test seam: clears in-memory state between cases. */
export function __resetLoggerForTests(): void {
  buffer.length = 0;
  flushPaused = false;
  flushesSinceTrim = 0;
  totalFlushes = 0;
  idleHandle = null;
  cancelDeferred();
  inFlight = null;
  correlationId = null;
  // A fresh "page load" for {@link anchorBatch}: a suite that reset the logger must be able to
  // exercise the first-flush repair, which is the only flush it happens on.
  generatorAnchored = false;
  options = { console: false, persist: false, now: () => Date.now() };
}
